import { useCallback } from 'react';
import { CATEGORY_COLORS } from '../../constants/colors';
import { getMostDistinctColor } from '../../../FlowchartView/utils/statusUtils';
import { detectCustomFieldConflicts, getExistingFieldNamesSet, getReservedFieldNames } from '../../../FlowchartView/hooks/customFieldConflictUtils';
import { generateId } from '../../../FlowchartView/hooks/clipboardUtils';
import { persistPastedNestedSprints } from '../../../FlowchartView/hooks/clipboardUtils/nestedSprintPersistence';
import { mergePastedTasksIntoSprint } from './pasteMergeUtils';
import {
  preparePastedNestedForUndo,
  trashPastedNestedRoots,
  restorePastedNestedSprints,
} from './pasteNestedUndo';
import { schedulePasteAutoLayout } from './schedulePasteAutoLayout';
import { applyHeldPositionsToFlowchart } from './applyPastePlacement';
import { scheduleParallelMissionLayout } from '../scheduleParallelMissionLayout';

function applyHostPatch(tasks, hostPatch) {
  if (!hostPatch?.id) return tasks;
  return (tasks || []).map((task) => {
    if (task.id !== hostPatch.id) return task;
    const next = { ...task, dependsOn: hostPatch.dependsOn };
    if (hostPatch.unlockConditions) next.unlockConditions = hostPatch.unlockConditions;
    return next;
  });
}

/**
 * usePasteTasks Hook
 * 
 * Handles pasting tasks from clipboard with support for:
 * - Tag definitions (preserving colors)
 * - Custom field definitions (with conflict detection)
 * - Nested sprints
 * - Undo/redo integration
 * 
 * @param {Object} options
 * @param {Function} options.onCustomFieldConflict - Optional async callback for conflict resolution.
 *   Called when a pasted field has the same name but different type as an existing field.
 *   Signature: ({ clipboardField, existingField, existingFieldNames }) => Promise<string|null>
 *   Returns new name to use, or null to skip the field.
 */
export const usePasteTasks = ({
  localSprint,
  setLocalSprint,
  flowchartRef,
  onAddSprint,
  onUpdateSprint,
  onTrashSprint,
  recordAction,
  onCustomFieldConflict,
}) => {
  // Handle pasting tasks (from clipboard)
  // tagDefinitions: optional array of { name, color } from clipboard for color preservation
  // customFieldDefinitions: optional array of custom field definitions from clipboard
  const handlePasteTasks = useCallback(async ({ 
    tasks: pastedTasks, 
    positions = {},
    nestedSprints: pastedNestedSprints, 
    tagDefinitions = [], 
    customFieldDefinitions = [],
    skipAutoLayout = false,
    hostPatch = null,
    centerOnTaskId = null,
  }) => {
    // Capture state before paste for undo
    const prevTasks = localSprint?.tasks || [];
    const prevTags = localSprint?.tags || localSprint?.categories || [];
    const prevCustomFields = localSprint?.customFields || [];
    const pastedTaskIds = pastedTasks.map(t => t.id);
    
    // ═══════════════════════════════════════════════════════════════
    // CUSTOM FIELD CONFLICT DETECTION
    // ═══════════════════════════════════════════════════════════════
    
    // Detect conflicts between clipboard fields and existing fields
    const { reusable, conflicts, reservedNameConflicts, newFields } = detectCustomFieldConflicts(
      customFieldDefinitions,
      prevCustomFields
    );
    
    // Build final custom field definitions with conflict resolution
    // Map: clipboardFieldId -> finalFieldId (for remapping task values)
    const fieldIdRemapping = new Map();
    const fieldsToAdd = [];
    
    // 1. Reusable fields: map to existing field ID
    for (const [clipboardIdx, existingId] of reusable.entries()) {
      const clipboardField = customFieldDefinitions[clipboardIdx];
      if (clipboardField?.id) {
        fieldIdRemapping.set(clipboardField.id, existingId);
      }
    }
    
    // 2. Handle conflicts: prompt user for new name or auto-rename
    const existingFieldNames = getExistingFieldNamesSet(prevCustomFields);
    const reservedNames = getReservedFieldNames();
    // Also include names of fields we're adding to avoid duplicate renames
    const allUsedNames = new Set([...existingFieldNames, ...reservedNames]);
    
    for (const conflict of conflicts) {
      const { clipboardField, existingField } = conflict;
      let newName = null;
      
      if (onCustomFieldConflict) {
        // Async callback: let the caller show a modal and wait for user input
        try {
          newName = await onCustomFieldConflict({
            clipboardField,
            existingField,
            existingFieldNames: allUsedNames,
          });
        } catch (e) {
          // User cancelled or error - skip this field
          console.log(`📋 Skipped conflicting field "${clipboardField.name}" (user cancelled)`);
          continue;
        }
      } else {
        // Auto-rename: append type to disambiguate
        newName = `${clipboardField.name} (${clipboardField.type})`;
        let suffix = 1;
        while (allUsedNames.has(newName.toLowerCase())) {
          suffix++;
          newName = `${clipboardField.name} (${clipboardField.type} ${suffix})`;
        }
        console.log(`📋 Auto-renamed conflicting field "${clipboardField.name}" to "${newName}"`);
      }
      
      if (newName) {
        // Create renamed field with new ID
        const timestamp = Date.now();
        const newFieldId = generateId('cf', timestamp, fieldsToAdd.length);
        
        // Remap option IDs for select fields
        let newOptions;
        if (clipboardField.type === 'select' && clipboardField.options) {
          newOptions = clipboardField.options.map((opt, idx) => ({
            ...opt,
            id: generateId('opt', timestamp, fieldsToAdd.length, idx),
          }));
        }
        
        fieldsToAdd.push({
          ...clipboardField,
          id: newFieldId,
          name: newName,
          options: newOptions,
        });
        
        fieldIdRemapping.set(clipboardField.id, newFieldId);
        allUsedNames.add(newName.toLowerCase());
      }
    }
    
    // 2b. Handle reserved name conflicts: fields using built-in field names
    for (const conflict of reservedNameConflicts) {
      const { clipboardField, reservedName } = conflict;
      let newName = null;
      
      if (onCustomFieldConflict) {
        // Use the same modal, but indicate it's a reserved name conflict
        try {
          newName = await onCustomFieldConflict({
            clipboardField,
            existingField: { name: reservedName, type: 'built-in' },
            existingFieldNames: allUsedNames,
          });
        } catch (e) {
          console.log(`📋 Skipped reserved name field "${clipboardField.name}" (user cancelled)`);
          continue;
        }
      } else {
        // Auto-rename: append type to disambiguate
        newName = `${clipboardField.name} (custom)`;
        let suffix = 1;
        while (allUsedNames.has(newName.toLowerCase())) {
          suffix++;
          newName = `${clipboardField.name} (custom ${suffix})`;
        }
        console.log(`📋 Auto-renamed reserved name field "${clipboardField.name}" to "${newName}"`);
      }
      
      if (newName) {
        const timestamp = Date.now();
        const newFieldId = generateId('cf', timestamp, fieldsToAdd.length);
        
        let newOptions;
        if (clipboardField.type === 'select' && clipboardField.options) {
          newOptions = clipboardField.options.map((opt, idx) => ({
            ...opt,
            id: generateId('opt', timestamp, fieldsToAdd.length, idx),
          }));
        }
        
        fieldsToAdd.push({
          ...clipboardField,
          id: newFieldId,
          name: newName,
          options: newOptions,
        });
        
        fieldIdRemapping.set(clipboardField.id, newFieldId);
        allUsedNames.add(newName.toLowerCase());
      }
    }
    
    // 3. New fields: add directly (check for name duplicates just in case)
    for (const { field: clipboardField } of newFields) {
      let fieldName = clipboardField.name;
      
      // Check if name is already used (shouldn't happen but be safe)
      if (allUsedNames.has(fieldName.toLowerCase())) {
        let suffix = 1;
        let newName = `${fieldName} (${suffix})`;
        while (allUsedNames.has(newName.toLowerCase())) {
          suffix++;
          newName = `${fieldName} (${suffix})`;
        }
        fieldName = newName;
        console.log(`📋 Renamed duplicate field "${clipboardField.name}" to "${fieldName}"`);
      }
      
      // Keep the clipboard field's ID (it's already unique from generateCustomFieldIds)
      fieldsToAdd.push({
        ...clipboardField,
        name: fieldName,
      });
      
      fieldIdRemapping.set(clipboardField.id, clipboardField.id);
      allUsedNames.add(fieldName.toLowerCase());
    }
    
    // 4. Remap custom field values in pasted tasks
    const remappedTasks = pastedTasks.map(task => {
      if (!task.customFieldValues || Object.keys(task.customFieldValues).length === 0) {
        return task;
      }
      
      const remappedValues = {};
      for (const [oldFieldId, value] of Object.entries(task.customFieldValues)) {
        const newFieldId = fieldIdRemapping.get(oldFieldId);
        if (newFieldId) {
          // For select fields, we also need to remap option IDs if the field was renamed
          // The options were regenerated with new IDs, so we need to find the matching option
          const oldField = customFieldDefinitions.find(f => f.id === oldFieldId);
          const newField = fieldsToAdd.find(f => f.id === newFieldId);
          
          if (oldField?.type === 'select' && newField?.options && typeof value === 'string') {
            // Find the option index in the old field and map to new option ID
            const oldOptIdx = oldField.options?.findIndex(o => o.id === value);
            if (oldOptIdx !== undefined && oldOptIdx >= 0 && newField.options[oldOptIdx]) {
              remappedValues[newFieldId] = newField.options[oldOptIdx].id;
            }
          } else if (oldFieldId !== newFieldId) {
            // For non-select fields or when using existing field, just remap the field ID
            remappedValues[newFieldId] = value;
          } else {
            // Field ID unchanged, keep value as-is
            remappedValues[newFieldId] = value;
          }
        }
        // If no mapping, the field was skipped (conflict not resolved)
      }
      
      return {
        ...task,
        customFieldValues: Object.keys(remappedValues).length > 0 ? remappedValues : undefined,
      };
    });
    
    // ═══════════════════════════════════════════════════════════════
    // ADD PASTED TASKS TO SPRINT
    // ═══════════════════════════════════════════════════════════════
    
    const mergedSprint = mergePastedTasksIntoSprint(localSprint, {
      pastedTasks: remappedTasks,
      tagDefinitions,
      fieldsToAdd,
    });
    if (!mergedSprint) return;

    const hostBefore = hostPatch?.id
      ? prevTasks.find((task) => task.id === hostPatch.id) || null
      : null;
    const updatedSprint = hostPatch
      ? { ...mergedSprint, tasks: applyHostPatch(mergedSprint.tasks, hostPatch) }
      : mergedSprint;

    setLocalSprint(updatedSprint);

    // Hold paste-here / (+) paste coords before any await so implicit ELK
    // sees customPositions on the first layout pass.
    if (skipAutoLayout) {
      applyHeldPositionsToFlowchart(flowchartRef, positions);
    }

    // Persist nested sprints (parents before children), then sync parent to global state
    await persistPastedNestedSprints({
      nestedSprints: pastedNestedSprints,
      rootParentId: localSprint?.id,
      onAddSprint,
      onUpdateSprint,
      parentSprint: updatedSprint,
    });
    
    console.log(`📋 Pasted ${remappedTasks.length} task(s)`);
    if (centerOnTaskId) {
      scheduleParallelMissionLayout(flowchartRef, centerOnTaskId);
    } else if (!skipAutoLayout) {
      schedulePasteAutoLayout(flowchartRef);
    }
    
    // Record for undo as a single action
    // Capture the final state for redo (with conflict-resolved fields)
    const finalFieldsToAdd = [...fieldsToAdd];
    const finalRemappedTasks = [...remappedTasks];
    const rootParentId = localSprint?.id;
    const { snapshots: nestedSnapshots, rootIds: nestedRootIds } = preparePastedNestedForUndo(
      pastedNestedSprints,
      rootParentId,
    );
    
    if (recordAction) {
      const taskCount = remappedTasks.length;
      const taskNames = remappedTasks.slice(0, 3).map(t => t.name).join(', ');
      const description = taskCount === 1 
        ? `Paste: "${remappedTasks[0].name}"`
        : `Paste ${taskCount} tasks: ${taskNames}${taskCount > 3 ? '...' : ''}`;
      
      recordAction({
        type: 'PASTE_TASKS',
        description,
        undo: () => {
          // Remove all pasted tasks and restore original state
          setLocalSprint(prev => {
            if (!prev) return prev;
            const remaining = prev.tasks.filter(t => !pastedTaskIds.includes(t.id));
            return {
              ...prev,
              tasks: hostBefore
                ? remaining.map((task) => (task.id === hostBefore.id ? hostBefore : task))
                : remaining,
              tags: prevTags,
              categories: prevTags,
              customFields: prevCustomFields,
            };
          });
          // Also trash nested campaigns the paste created (otherwise they linger
          // in the sidebar with no mission cards).
          trashPastedNestedRoots(nestedRootIds, onTrashSprint);
        },
        redo: () => {
          // Re-add the pasted tasks with all the merged definitions
          setLocalSprint(prev => {
            if (!prev) return prev;
            const existingTasks = prev.tasks || [];
            
            // Re-add tasks with order (use remapped tasks)
            const tasksWithOrder = finalRemappedTasks.map((task, idx) => ({
              ...task,
              order: existingTasks.length + idx + 1,
            }));
            
            // Re-merge tags
            let updatedTags = prev.tags || prev.categories || [];
            const existingTagNames = new Set(updatedTags.map(t => t.name));
            for (const tagName of new Set(finalRemappedTasks.flatMap(t => t.tags || [t.category]).filter(Boolean))) {
              if (!existingTagNames.has(tagName)) {
                const clipboardTag = tagDefinitions.find(t => t.name === tagName);
                const color = clipboardTag?.color || getMostDistinctColor(
                  updatedTags.map(t => t.color).filter(Boolean),
                  CATEGORY_COLORS
                );
                updatedTags = [...updatedTags, { name: tagName, color }];
              }
            }
            
            // Re-merge custom fields (use resolved fields)
            let updatedCustomFields = prev.customFields || [];
            const existingFieldIds = new Set(updatedCustomFields.map(f => f.id));
            for (const fieldToAdd of finalFieldsToAdd) {
              if (!existingFieldIds.has(fieldToAdd.id)) {
                updatedCustomFields = [...updatedCustomFields, {
                  ...fieldToAdd,
                  order: updatedCustomFields.length,
                }];
              }
            }
            
            return {
              ...prev,
              tags: updatedTags,
              categories: updatedTags,
              customFields: updatedCustomFields,
              tasks: applyHostPatch([...existingTasks, ...tasksWithOrder], hostPatch),
            };
          });
          
          if (skipAutoLayout) {
            applyHeldPositionsToFlowchart(flowchartRef, positions);
          }
          if (centerOnTaskId) {
            scheduleParallelMissionLayout(flowchartRef, centerOnTaskId);
          } else if (!skipAutoLayout) {
            schedulePasteAutoLayout(flowchartRef);
          }

          restorePastedNestedSprints(nestedSnapshots, rootParentId, {
            onUpdateSprint,
            onAddSprint,
          });
        },
      });
    }
  }, [setLocalSprint, localSprint, onAddSprint, onUpdateSprint, onTrashSprint, flowchartRef, recordAction, onCustomFieldConflict]);

  return { handlePasteTasks };
};

export default usePasteTasks;
